import React, { JSX, useCallback, useEffect, useRef, useState } from 'react';
import { useMutation } from 'react-query';
import { Button, Center, Flex, Text } from '@chakra-ui/react';
import { getRelevoByUId, verifyRelevosForUser } from 'api/relevos';
import { AxiosError } from 'axios';
import { Routes } from 'config/routes';
import { useTranslations } from 'contexts/LocalizationContext';
import { useScannedRelevosContext } from 'contexts/ScannedRelevosContext';
import { StatusCodes } from 'http-status-codes';

import { RelevoInterface } from '../../../../../api/types';
import Dialog from '../../../../../common/components/ConfirmationDialog/Dialog';
import { LinkButton } from '../../../../../common/components/LinkButton';
import QrCodeScanner from '../../../../../common/components/QrCodeScanner/QrCodeScanner';
import { ScanModal } from '../../../../../common/components/ScanModal/ScanModal';
import HandScanner from '../../../../../common/components/Scanner/HandScanner';
import { useScanModalFlow } from '../../../../../common/components/Scanner/hooks/use-scan-modal-flow.hook';
import { HttpException } from '../../../../../common/models/httpException';
import { extractUId, extractUserId } from '../../../../../utils/qrCodesHelper';
import { QuickContactType } from '../../../../QuickContacts/model/enums/quick-contact-type.enum';
import { UpgradeErrorCodes } from '../../../components/Upgrade/utils/upgrade-error-codes';
import { ItemAssignmentSelectMode } from '../../../components/UserSelect/types';
import { ScannerMode } from '../../../model/enums/scanner-mode.enum';

const RelevoScanner = (): JSX.Element | null => {
  const translations = useTranslations();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { selectItemMode, scannedRelevos, setScannedRelevos, applicationUser, quickContact, mode, restaurantId } =
    useScannedRelevosContext();
  const cancelRef = useRef(null);
  const [code, setCode] = useState('');
  const {
    isOpen,
    onClose: handleModalClose,
    modalKey: scanModalKey,
    scanStatus,
    scannedItem,
    setScannedItem,
    openSuccess,
    openError,
    handleDuplicate,
  } = useScanModalFlow<RelevoInterface>();

  const handleResponseError = (err: AxiosError<HttpException>) => {
    if (
      err.response?.status === StatusCodes.BAD_REQUEST &&
      err.response.data.error === UpgradeErrorCodes.CARD_USER_NOT_ENOUGH_FUNDS
    ) {
      setIsModalOpen(true);
      return;
    }
  };

  const { mutate: getRelevo, isLoading: isLoadingGetRelevo } = useMutation(getRelevoByUId, {
    onSuccess: (response) => {
      setCode('');
      openSuccess(response, response.uId ?? null);
      setScannedRelevos((relevos) => [...relevos, response]);
    },
    onError: (err: AxiosError<HttpException>) => {
      handleResponseError(err);
      openError();
      setCode('');
    },
  });

  const { mutate: verifyRelevos, isLoading: isLoadingVerifyRelevos } = useMutation(verifyRelevosForUser, {
    onSuccess: (response) => {
      setScannedRelevos(response);
      const lastItem = response[response.length - 1];
      openSuccess(lastItem ?? null, lastItem?.uId ?? null);
      if (response.some((item) => item.uId === extractUId(code))) {
        setCode('');
      }
    },
    onError: (err: AxiosError<HttpException>) => {
      handleResponseError(err);
      openError();
      setCode('');
    },
  });

  const isAlreadyScanned = useCallback(
    (uId: string | null): boolean => {
      return scannedRelevos.some((item) => item.uId === uId);
    },
    [scannedRelevos],
  );

  const handleScan = async (qrCode: string | null) => {
    if (!qrCode) {
      return;
    }
    const userId = extractUserId(qrCode);
    // Ignore scanning the current application user's userId during item scanning (step 2) in assign mode
    if (mode === ScannerMode.ASSIGN && applicationUser?.userId && userId === applicationUser.userId) {
      setCode('');
      return;
    }
    const uId = extractUId(qrCode);

    if (!uId) {
      openError();
      setCode('');
      return;
    }
    if (isAlreadyScanned(uId)) {
      const item = scannedRelevos.find((relevo) => relevo.uId === uId) ?? null;
      setScannedItem(item);
      handleDuplicate(uId, item);
      setCode('');
      return;
    }
    if (isLoadingGetRelevo || isLoadingVerifyRelevos) {
      setCode('');
      return;
    }
    if (mode === ScannerMode.ASSIGN) {
      if (!applicationUser && !quickContact) {
        setCode('');
        return;
      } else {
        const relevosUIds = scannedRelevos.map((item) => item.uId);
        if ((applicationUser || quickContact?.type === QuickContactType.USER) && restaurantId) {
          verifyRelevos({
            userId: applicationUser?.id || quickContact?.id || '',
            previouslyScannedProductsUids: relevosUIds,
            uId,
            restaurantId,
          });
          setCode('');
        } else if (quickContact?.type === QuickContactType.RESTAURANT) {
          getRelevo(uId);
          setCode('');
        } else {
          setCode('');
        }
      }
    } else {
      getRelevo(uId);
      setCode('');
    }
  };

  const handleScanRef = useRef(handleScan);
  handleScanRef.current = handleScan;

  // Consider using useEffectEvent once it is released in a stable version of React, and we upgrade it
  // https://react.dev/learn/separating-events-from-effects#declaring-an-effect-event
  useEffect(() => {
    if (code.length) {
      void handleScanRef.current(code);
    }
  }, [code]);

  return (
    <Flex w={'100%'}>
      <Flex py={0} p={6} h="100%" my={'auto'}>
        <Center m={'auto'} p={2}>
          {[
            ScannerMode.CARD_UPGRADE,
            ScannerMode.DEPOSIT_ASSIGN,
            ScannerMode.DEPOSIT_RETURN,
            ScannerMode.REPORT_DAMAGED_ITEM,
          ].includes(mode) ? (
            <QrCodeScanner onScan={setCode} />
          ) : null}
          {[ScannerMode.ASSIGN, ScannerMode.RETURN].includes(mode) &&
          selectItemMode === ItemAssignmentSelectMode.CAMERA_SCAN ? (
            <QrCodeScanner onScan={setCode} />
          ) : null}
          {[ScannerMode.ASSIGN, ScannerMode.RETURN].includes(mode) &&
          selectItemMode === ItemAssignmentSelectMode.HAND_SCANNER_SCAN ? (
            <HandScanner onScan={setCode} />
          ) : null}
        </Center>
      </Flex>

      <ScanModal
        key={scanModalKey}
        isOpen={isOpen}
        onClose={handleModalClose}
        status={scanStatus}
        item={scannedItem ?? undefined}
      />

      <Dialog
        leastDestructiveRef={cancelRef}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        dialogFooter={
          <>
            <Button
              variant="transparent"
              color="orange.500"
              minWidth={144}
              margin="auto"
              ref={cancelRef}
              onClick={() => {
                setIsModalOpen(false);
              }}
              title={translations('cancel')}
            >
              {translations('cancel')}
            </Button>
            <LinkButton
              variant="orangeSolid"
              to={`${Routes.RELEVO_CARD_UPGRADE}`}
              minWidth={144}
              margin="auto"
              title={translations('yes_please')}
              ml={3}
            >
              {translations('yes_please')}
            </LinkButton>
          </>
        }
      >
        <Text>{translations('balance_limit_reached')}</Text>
      </Dialog>
    </Flex>
  );
};

export default RelevoScanner;
